import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaChevronDown,
  FaChevronUp,
  FaCode,
  FaComments,
  FaCube,
  FaEllipsisH,
  FaGamepad,
  FaLayerGroup,
  FaPalette,
  FaPencilAlt,
  FaPlus,
  FaSpinner,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import { userService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import {
  MAX_SKILLS,
  SKILL_CATEGORIES,
  SKILL_NAME_MAX,
  getCategoryMeta,
  normalizeSkillCategory,
  normalizeSkillLevel,
  normalizeSkillName,
  serializeSkills,
  skillsEqual,
} from '../../utils/skillsClient';
import './SkillsShowcase.css';

const CATEGORY_ICONS = {
  dev: FaCode,
  design: FaPalette,
  build: FaCube,
  game: FaGamepad,
  soft: FaComments,
  other: FaEllipsisH,
};

function getTranslatedSkillLevelLabel(level, t) {
  const value = normalizeSkillLevel(level, 0);
  if (value >= 90) return t('skills.level.expert');
  if (value >= 75) return t('skills.level.advanced');
  if (value >= 50) return t('skills.level.intermediate');
  if (value >= 25) return t('skills.level.basic');
  return t('skills.level.beginner');
}

function CategoryIcon({ id, ...props }) {
  const Icon = CATEGORY_ICONS[id] || FaEllipsisH;
  return <Icon {...props} />;
}

function SkillsEditorModal({
  open,
  initialSkills,
  busy,
  onClose,
  onSave,
}) {
  const { t, i18n } = useTranslation();
  const nameInputRef = useRef(null);
  const [skills, setSkills] = useState(() => serializeSkills(initialSkills));
  const [categoryId, setCategoryId] = useState('dev');
  const [draft, setDraft] = useState('');
  const [draftLevel, setDraftLevel] = useState(70);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setSkills(serializeSkills(initialSkills));
    setDraft('');
    setDraftLevel(70);
    setError('');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => {
      nameInputRef.current?.focus();
    }, 120);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(focusTimer);
    };
  }, [open, initialSkills, busy, onClose]);

  const selectedCategory = useMemo(
    () => getCategoryMeta(categoryId),
    [categoryId]
  );

  const ownedNames = useMemo(
    () => new Set(skills.map((skill) => skill.name.toLowerCase())),
    [skills]
  );

  const categorySuggestions = useMemo(() => {
    const translated = t(`skills.categories.${categoryId}.suggestions`, { returnObjects: true });
    if (Array.isArray(translated) && translated.length > 0) return translated;
    return selectedCategory.suggestions;
  }, [t, i18n.language, categoryId, selectedCategory]);

  const suggestions = useMemo(
    () => categorySuggestions.filter(
      (name) => !ownedNames.has(name.toLowerCase())
    ),
    [ownedNames, categorySuggestions]
  );

  const addSkill = (rawName, rawCategory = categoryId, level = draftLevel) => {
    const name = normalizeSkillName(rawName);
    if (!name || name.length < 2) {
      setError(t('skills.errorMinChars'));
      return;
    }
    if (skills.length >= MAX_SKILLS) {
      setError(t('skills.errorMaxSkills', { count: MAX_SKILLS }));
      return;
    }
    if (ownedNames.has(name.toLowerCase())) {
      setError(t('skills.errorDuplicate'));
      return;
    }
    setSkills((current) => [
      ...current,
      {
        name,
        category: normalizeSkillCategory(rawCategory),
        level: normalizeSkillLevel(level, 70),
      },
    ]);
    setDraft('');
    setError('');
  };

  const updateSkill = (index, patch) => {
    setSkills((current) => current.map((skill, skillIndex) => (
      skillIndex === index ? { ...skill, ...patch } : skill
    )));
  };

  const removeSkill = (index) => {
    setSkills((current) => current.filter((_, skillIndex) => skillIndex !== index));
  };

  const moveSkill = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= skills.length) return;
    setSkills((current) => {
      const next = [...current];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  const dirty = !skillsEqual(skills, initialSkills);
  const atLimit = skills.length >= MAX_SKILLS;
  const average = skills.length
    ? Math.round(skills.reduce((sum, skill) => sum + skill.level, 0) / skills.length)
    : 0;
  const fillRatio = Math.round((skills.length / MAX_SKILLS) * 100);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="skills-modal-backdrop"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!busy) onClose();
          }}
        >
          <motion.div
            className="skills-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="skills-modal-title"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="skills-modal-head">
              <div className="skills-modal-head-copy">
                <p className="skills-modal-kicker">{t('skills.modalKicker')}</p>
                <h3 id="skills-modal-title">{t('skills.modalTitle')}</h3>
                <p>{t('skills.modalLead')}</p>
              </div>
              <div className="skills-modal-head-aside">
                <div className="skills-modal-capacity" aria-hidden="true">
                  <div
                    className="skills-modal-capacity-ring"
                    style={{ '--fill': `${fillRatio}%` }}
                  >
                    <span className="skills-modal-capacity-value">
                      <strong>{skills.length}</strong>
                      <em>/{MAX_SKILLS}</em>
                    </span>
                  </div>
                  <small>{t('skills.slotsUsed', { count: skills.length, max: MAX_SKILLS })}</small>
                </div>
                <button
                  type="button"
                  className="skills-icon-btn"
                  onClick={onClose}
                  disabled={busy}
                  aria-label={t('skills.close')}
                >
                  <FaTimes />
                </button>
              </div>
            </header>

            <div className="skills-modal-layout">
              <section className={`skills-modal-composer is-${categoryId}`} aria-label={t('skills.composerTitle')}>
                <div className="skills-modal-pane-head">
                  <span>{t('skills.composerTitle')}</span>
                </div>

                <div className="skills-modal-categories" role="tablist" aria-label={t('skills.categoriesAria')}>
                  {SKILL_CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      role="tab"
                      aria-selected={categoryId === category.id}
                      className={`skills-modal-category is-${category.id}${categoryId === category.id ? ' is-active' : ''}`}
                      onClick={() => setCategoryId(category.id)}
                      disabled={busy}
                    >
                      <CategoryIcon id={category.id} aria-hidden="true" />
                      {t(`skills.categories.${category.id}.label`)}
                    </button>
                  ))}
                </div>
                <p className="skills-modal-hint">{t(`skills.categories.${categoryId}.hint`)}</p>

                <label className="skills-modal-field">
                  <span className="skills-modal-field-label">
                    {t('skills.skillName')}
                    <em>{draft.length}/{SKILL_NAME_MAX}</em>
                  </span>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={draft}
                    maxLength={SKILL_NAME_MAX}
                    disabled={busy || atLimit}
                    placeholder={t('skills.placeholderExample', {
                      example: categorySuggestions[0] || t('skills.categories.dev.suggestions.0', { defaultValue: 'Developer' }),
                    })}
                    onChange={(event) => {
                      setDraft(event.target.value);
                      if (error) setError('');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addSkill(draft);
                      }
                    }}
                  />
                </label>

                <div className="skills-modal-mastery">
                  <div className="skills-modal-mastery-meta">
                    <span>{t('skills.mastery')}</span>
                    <div>
                      <strong>{draftLevel}%</strong>
                      <small>{getTranslatedSkillLevelLabel(draftLevel, t)}</small>
                    </div>
                  </div>
                  <input
                    className="skills-range"
                    type="range"
                    min="1"
                    max="100"
                    value={draftLevel}
                    disabled={busy}
                    style={{ '--pct': `${draftLevel}%` }}
                    aria-label={t('skills.mastery')}
                    onChange={(event) => setDraftLevel(Number(event.target.value))}
                  />
                </div>

                {error ? <p className="skills-modal-error">{error}</p> : null}

                <button
                  type="button"
                  className="skills-modal-add"
                  disabled={busy || atLimit || !draft.trim()}
                  onClick={() => addSkill(draft)}
                >
                  <FaPlus aria-hidden="true" /> {t('skills.add')}
                </button>

                {suggestions.length > 0 && (
                  <div className="skills-modal-suggestions">
                    <span className="skills-modal-field-label">{t('skills.quickAdd')}</span>
                    <div className="skills-modal-suggestions-row">
                      {suggestions.slice(0, 8).map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={`skills-suggestion is-${categoryId}`}
                          disabled={busy || atLimit}
                          onClick={() => addSkill(name, categoryId, draftLevel)}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="skills-modal-stack" aria-label={t('skills.yourStack')}>
                <div className="skills-modal-pane-head">
                  <strong>{t('skills.yourStack')}</strong>
                  <span>{t('skills.slotsUsed', { count: skills.length, max: MAX_SKILLS })}</span>
                </div>
                <p className="skills-modal-stack-hint">{t('skills.featuredHint')}</p>

                {skills.length === 0 ? (
                  <div className="skills-modal-empty">
                    <span className="skills-modal-empty-icon" aria-hidden="true">
                      <FaLayerGroup />
                    </span>
                    <strong>{t('skills.emptyListTitle')}</strong>
                    <p>{t('skills.emptyListText')}</p>
                  </div>
                ) : (
                  <ul className="skills-modal-list">
                    {skills.map((skill, index) => (
                      <li
                        key={`${skill.category}-${skill.name}`}
                        className={`skills-modal-item is-${skill.category}${index < 3 ? ' is-featured' : ''}`}
                      >
                        <div className="skills-modal-item-top">
                          <span className="skills-modal-item-index">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="skills-modal-item-copy">
                            <div className="skills-modal-item-title">
                              <strong>{skill.name}</strong>
                              {index < 3 ? (
                                <em>{t('skills.featured')}</em>
                              ) : null}
                            </div>
                            <small>
                              <CategoryIcon id={skill.category} aria-hidden="true" />
                              {t(`skills.categories.${skill.category}.label`)}
                              {' · '}
                              {getTranslatedSkillLevelLabel(skill.level, t)}
                            </small>
                          </div>
                          <div className="skills-modal-item-actions">
                            <button
                              type="button"
                              disabled={busy || index === 0}
                              onClick={() => moveSkill(index, index - 1)}
                              aria-label={t('skills.moveUp')}
                            >
                              <FaChevronUp />
                            </button>
                            <button
                              type="button"
                              disabled={busy || index === skills.length - 1}
                              onClick={() => moveSkill(index, index + 1)}
                              aria-label={t('skills.moveDown')}
                            >
                              <FaChevronDown />
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              disabled={busy}
                              onClick={() => removeSkill(index)}
                              aria-label={t('skills.remove')}
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </div>
                        <div className="skills-modal-item-level">
                          <div className="skills-modal-item-level-meta">
                            <span>{t('skills.mastery')}</span>
                            <strong>{skill.level}%</strong>
                          </div>
                          <input
                            className="skills-range"
                            type="range"
                            min="1"
                            max="100"
                            value={skill.level}
                            disabled={busy}
                            style={{ '--pct': `${skill.level}%` }}
                            aria-label={`${skill.name} ${t('skills.mastery')}`}
                            onChange={(event) => updateSkill(index, {
                              level: normalizeSkillLevel(event.target.value, skill.level),
                            })}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <footer className="skills-modal-footer">
              <div className="skills-modal-foot-meta">
                {dirty ? (
                  <span className="is-dirty">{t('skills.unsaved')}</span>
                ) : (
                  <span>
                    {skills.length > 0
                      ? `${t('skills.averageMastery')} ${average}%`
                      : t('skills.slotsUsed', { count: skills.length, max: MAX_SKILLS })}
                  </span>
                )}
              </div>
              <div className="skills-modal-foot-actions">
                <button type="button" className="skills-btn is-ghost" disabled={busy} onClick={onClose}>
                  {t('skills.cancel')}
                </button>
                <button
                  type="button"
                  className="skills-btn"
                  disabled={busy || !dirty}
                  onClick={() => onSave(skills)}
                >
                  {busy ? <FaSpinner className="social-spin" /> : null}
                  {busy ? t('skills.saving') : t('skills.saveStack')}
                </button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default function SkillsShowcase({
  skills = [],
  isOwner = false,
  displayName,
  onSkillsChange,
  onStatus,
}) {
  const { t } = useTranslation();
  const { updateUser } = useAuth();
  const list = serializeSkills(skills);
  const [editorOpen, setEditorOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const resolvedDisplayName = displayName || t('skills.thisUser');

  const featured = list.slice(0, 3);
  const rest = list.slice(3);
  const average = list.length
    ? Math.round(list.reduce((sum, skill) => sum + skill.level, 0) / list.length)
    : 0;

  const handleSave = async (nextSkills) => {
    setBusy(true);
    try {
      const response = await userService.updateMe({
        profile: { skills: nextSkills },
      });
      const nextUser = response?.data?.user || null;
      if (nextUser) updateUser(nextUser);
      const saved = serializeSkills(nextUser?.profile?.skills || nextSkills);
      onSkillsChange?.(saved);
      setEditorOpen(false);
      onStatus?.({ type: 'success', message: t('skills.saved') });
    } catch (error) {
      onStatus?.({ type: 'error', message: error.message || t('skills.saveFailed') });
    } finally {
      setBusy(false);
    }
  };

  if (list.length === 0) {
    if (!isOwner) return null;
    return (
      <>
        <div className="skills-showcase is-empty">
          <div>
            <span className="skills-showcase-eyebrow">{t('skills.eyebrow')}</span>
            <strong>{t('skills.emptyTitle')}</strong>
            <p>
              {t('skills.emptyText')}
            </p>
          </div>
          <button type="button" className="skills-showcase-cta" onClick={() => setEditorOpen(true)}>
            <FaPlus aria-hidden="true" /> {t('skills.buildCta')}
          </button>
        </div>
        <SkillsEditorModal
          open={editorOpen}
          initialSkills={list}
          busy={busy}
          onClose={() => {
            if (!busy) setEditorOpen(false);
          }}
          onSave={handleSave}
        />
      </>
    );
  }

  return (
    <>
      <section className="skills-showcase" aria-label={t('skills.ariaLabel', { name: resolvedDisplayName })}>
        <div className="skills-showcase-head">
          <div className="skills-showcase-head-top">
            <span className="skills-showcase-eyebrow">{t('skills.eyebrow')}</span>
            <div className="skills-showcase-head-actions">
              <span className="skills-showcase-count">{list.length}</span>
              {isOwner && (
                <button
                  type="button"
                  className="skills-showcase-edit"
                  onClick={() => setEditorOpen(true)}
                >
                  <FaPencilAlt aria-hidden="true" /> {t('skills.edit')}
                </button>
              )}
            </div>
          </div>
          <h3>{t('skills.specialties')}</h3>
          <p className="skills-showcase-lead">
            {t('skills.averageMastery')} <strong>{average}%</strong>
          </p>
        </div>

        <ul className="skills-showcase-featured">
          {featured.map((skill, index) => (
            <motion.li
              key={`featured-${skill.name}`}
              className={`skills-showcase-card is-${skill.category}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: index * 0.06 }}
            >
              <div className="skills-showcase-card-copy">
                <span className="skills-showcase-index">0{index + 1}</span>
                <strong>{skill.name}</strong>
                <div className="skills-showcase-card-meta">
                  <small>
                    {t(`skills.categories.${skill.category}.label`)}
                    {' · '}
                    {getTranslatedSkillLevelLabel(skill.level, t)}
                  </small>
                  <span>{skill.level}%</span>
                </div>
                <div className="skills-showcase-card-track" aria-hidden="true">
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.level}%` }}
                    transition={{
                      duration: 0.75,
                      delay: 0.08 + index * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />
                </div>
              </div>
            </motion.li>
          ))}
        </ul>

        {rest.length > 0 && (
          <ul className="skills-showcase-bars">
            {rest.map((skill, index) => (
              <motion.li
                key={`bar-${skill.name}`}
                className={`skills-showcase-bar is-${skill.category}`}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.28, delay: 0.18 + index * 0.04 }}
              >
                <div className="skills-showcase-bar-head">
                  <strong>{skill.name}</strong>
                  <span>{skill.level}%</span>
                </div>
                <div className="skills-showcase-bar-track" aria-hidden="true">
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.level}%` }}
                    transition={{ duration: 0.7, delay: 0.2 + index * 0.04, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <small>{getTranslatedSkillLevelLabel(skill.level, t)}</small>
              </motion.li>
            ))}
          </ul>
        )}
      </section>

      {isOwner && (
        <SkillsEditorModal
          open={editorOpen}
          initialSkills={list}
          busy={busy}
          onClose={() => {
            if (!busy) setEditorOpen(false);
          }}
          onSave={handleSave}
        />
      )}
    </>
  );
}
